import Mousetrap from "mousetrap";
import PropTypes from "prop-types";
import React from "react";
import { Grid } from "react-bootstrap";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";

import icon from "../img/Boat.svg";

import FeatureFlagActions from "./actions/FeatureFlagActions";
import UserActions from "./actions/UserActions";

import Accounts from "./components/Accounts";
import AppNavigation from "./components/AppNavigation";
import Budget from "./components/Budget";
import BudgetTemplates from "./components/BudgetTemplates";
import Categories from "./components/Categories";
import ContactUs from "./components/ContactUs";
import Importer from "./components/Importer";
import Settings from "./components/Settings";
import Subscription from "./components/Subscription";
import Tags from "./components/Tags";
import Currencies from "./components/Currencies";
import Transactions from "./components/Transactions";
import UpgradeForm from "./components/UpgradeForm";

import ApiAuthorizationRoutes from "./components/api-authorization/ApiAuthorizationRoutes";
import { ApplicationPaths } from "./components/api-authorization/ApiAuthorizationConstants";
import AuthorizeRoute from "./components/api-authorization/AuthorizeRoute";
import AuthorizeService from "./components/api-authorization/AuthorizeService";

import FeatureFlagStore from "./stores/FeatureFlagStore";
import UserStore from "./stores/UserStore";

export default class App extends React.Component
{
	constructor(props)
	{
		super(props);
		this.state = App.getStoreState();
		this.state.loading = true;
		this.state.isAdminUser = false;
		this.keyBindings = [];

		FeatureFlagActions.initialize(this.props.featureFlags, this.props.features);

		this._onChange = this._onChange.bind(this);
		this.handleShowHelp = this.handleShowHelp.bind(this);
	}

	static getStoreState()
	{
		// NOTE: if this method changes then _onChange may need changes as well
		const userProfile = UserStore.getUserProfile();
		let homepage = "/transactions"; // default home page
		if (userProfile && userProfile.homepage)
		{
			homepage = userProfile.homepage;
		}

		return {homepage};
	}

	handleShowHelp()
	{
		if (this.keyBindings && this.keyBindings.length >= 1)
		{
			let bindings = this.keyBindings.map(binding => `${binding.key}: ${binding.description}`).join(",\n");
			if (this.props.version && this.state.isAdminUser)
			{
				bindings += `\n\nVersion: ${this.props.version}`;
			}
			alert(bindings);
		}
		// Else there are no keybindings to show
	}

	_onChange()
	{
		const newState = App.getStoreState();
		if (newState.homepage !== this.state.homepage)
		{
			// Only update if changed
			this.setState({ homepage: newState.homepage });
		}
	}

	componentDidMount()
	{
		const originalCallback = Mousetrap.prototype.stopCallback;
		Mousetrap.prototype.stopCallback = function (event, element, combo) {
			if (combo.indexOf("mod") !== -1 && element.classList.contains("mousetrap-mod"))
			{
				return false;
			}
			return originalCallback.call(this, event, element, combo);
		};
		const originalBind = Mousetrap.prototype.bind;
		const appThis = this;
		Mousetrap.prototype.bind = function (key, description, ...rest) {
			if (typeof description === "function") {
				// This means that the bind method was not called with a description
				return originalBind.call(this, key, description, ...rest);
			}
			appThis.keyBindings.push({ key, description });
			return originalBind.call(this, key, ...rest);
		};
		const originalUnbind = Mousetrap.prototype.unbind;
		Mousetrap.prototype.unbind = function (key, ...rest) {
			const index = appThis.keyBindings.findIndex(binding => binding.key === key);
			if (index !== -1) {
				appThis.keyBindings.splice(index, 1);
			}
			return originalUnbind.call(this, key, ...rest);
		};

		Mousetrap.bind(["?", "/"], "Help", this.handleShowHelp);

		this.makeRemoteRequest();
	}

	async makeRemoteRequest()
	{
		const authenticated = await AuthorizeService.isAuthenticated();
		if (authenticated)
		{
			const isAdminUser = await AuthorizeService.isAdminUser();
			const userProfile = await UserActions.loadUserProfile();
			let homepage = this.state.homepage;
			if (userProfile && userProfile.homepage)
			{
				homepage = userProfile.homepage;
			}
			this.setState({loading: false, authenticated, homepage, isAdminUser});
		}
		else
		{
			this.setState({loading: false, authenticated});
		}

		// Adding this after the above avoids the re-render the first time, since we already have the userProfile
		UserStore.addChangeListener(this._onChange);
	}

	componentWillUnmount()
	{
		UserStore.removeChangeListener(this._onChange);
		Mousetrap.unbind(["?", "/"]);
	}

	render()
	{
		const { loading, homepage } = this.state;
		const premiumAccountEnabled = FeatureFlagStore.premiumAccountEnabled();
		const importEnabled = FeatureFlagStore.importEnabled();
		const contactUsEnabled = FeatureFlagStore.contactUsEnabled();

		if (loading)
		{
			return (
				<div className="loader">
					<img src={icon} />
				</div>
			);
		}

		return (
			<BrowserRouter>
				<div>
					<AppNavigation {...this.props} handleShowHelp={this.handleShowHelp} />
					<Grid>
						<Switch>
							<AuthorizeRoute path="/" exact component={Redirect} componentProps={{to:homepage}} />
							<AuthorizeRoute path="/accounts/:accountName?" component={Accounts} />
							<AuthorizeRoute path="/transactions/:accountName?" component={Transactions} />
							<AuthorizeRoute path="/categories" component={Categories} />
							<AuthorizeRoute path="/tags" component={Tags} />
							<AuthorizeRoute path="/budget" component={Budget} />
							<AuthorizeRoute path="/budgettemplates" component={BudgetTemplates} />
							<AuthorizeRoute path="/settings" component={Settings} />
							<AuthorizeRoute path="/currencies" component={Currencies} />
							{importEnabled && <AuthorizeRoute path="/import" component={Importer} />}
							{premiumAccountEnabled && <AuthorizeRoute path="/upgrade" component={UpgradeForm} />}
							{premiumAccountEnabled && <AuthorizeRoute path="/subscription" component={Subscription} />}
							{contactUsEnabled && <AuthorizeRoute path="/contactus" component={ContactUs} />}
							<Route path={ApplicationPaths.ApiAuthorizationPrefix} component={ApiAuthorizationRoutes} />
							<Redirect to="/" />
						</Switch>
					</Grid>
				</div>
			</BrowserRouter>
		);
	}
}

App.propTypes = {
	featureFlags: PropTypes.object,
	features: PropTypes.object,
	version: PropTypes.string
};


// WEBPACK FOOTER //
// ./Components/App.jsx